/**
 * Graph View — Phase 5
 *
 * Force-directed D3.js visualisation of the corpus, in two shapes. Which one is
 * drawn comes from the server (`GET /api/graph?mode=`) and is carried per node
 * as `kind`:
 *
 *   - `mode=signals` — signals are nodes, joined where they share a wikilink.
 *     Every node is `kind: "signal"`. Edges are pairwise, so they grow with the
 *     *square* of the window; the server caps both nodes and edges and reports
 *     what it dropped.
 *   - `mode=tags` — signals *and* wikilinks are nodes (`kind: "signal"` and
 *     `kind: "wikilink"`), joined by which signal carries which tag. Edges are
 *     memberships, so they grow linearly and this view holds a far larger
 *     window. Tag nodes are sized by how many of the drawn signals carry them.
 *
 * Desktop: full-screen SVG canvas with floating controls + filter panel.
 * Mobile:  desktop-only notice (graph not supported on mobile).
 *
 * **Two force configurations, chosen by size.** PRD §5.5's constants
 * (iterations 500, centerForce 0.005, repulsionStrength 8000,
 * attractionStrength 0.0008, damping 0.85, minDistance 80) were tuned against a
 * corpus of about a dozen signals and still apply below `LARGE_GRAPH_NODES`.
 * Above it — which `mode=tags` reaches routinely — they blow the layout apart,
 * so `drawGraph` switches to conventional large-graph forces, labels only tags,
 * and scales the synchronous tick budget down from 500. See `drawGraph`.
 *
 * Rendering is SVG in both modes; Canvas mode is not implemented. The bound
 * that matters is total elements rather than nodes alone — one `<line>` per
 * edge, and edges outnumber nodes by orders of magnitude in `mode=signals`.
 */

import * as d3 from "d3";
import { api } from "../lib/api";
import { ensureAppTimezone, todayInAppTz } from "../lib/datetime";
import { renderHeader } from "../components/header";
import { navigate, registerPageCleanup } from "../lib/router";

// ── Types ────────────────────────────────────────────────────────────────────

type GraphMode = "signals" | "tags";

interface GraphNode {
  id: string;
  /** "signal" in both modes; "wikilink" only in `tags`, where tags are nodes too. */
  kind: "signal" | "wikilink";
  title: string;
  wikilinks: string[];
  size: number;
  datePublished: string;
}

interface GraphEdge {
  source: string;
  target: string;
  weight: number;
  sharedWikilinks: string[];
}

interface GraphData {
  mode: GraphMode;
  nodes: GraphNode[];
  edges: GraphEdge[];
  /** Signals matching the filters, before the server's node cap. */
  total: number;
  capped: boolean;
  /** Edges built, before the server's edge cap. */
  edgeTotal: number;
  edgesCapped: boolean;
}

// D3 simulation mutates these in-place (adds x, y, vx, vy, fx, fy)
interface NodeDatum extends GraphNode, d3.SimulationNodeDatum {}

// After forceLink resolves references, source/target are NodeDatum objects.
// We keep the union type D3 requires and cast in asNode().
interface EdgeDatum extends d3.SimulationLinkDatum<NodeDatum> {
  weight: number;
  sharedWikilinks: string[];
}

interface FilterState {
  mode: GraphMode;
  from: string;
  to: string;
  wikilinks: string[];
  workstreams: string[];
  steeples_dim: string;
  steeples_min: number;
}

interface WikilinkItem { id: string; name: string; signalCount: number; }
interface WorkstreamItem { id: string; name: string; }

// ── Constants (PRD §5.5) ─────────────────────────────────────────────────────

const FORCE_CONFIG = {
  iterations: 500,
  centerForce: 0.005,
  repulsionStrength: 8000,
  attractionStrength: 0.0008,
  damping: 0.85,
  minDistance: 80,
} as const;

/**
 * Above this many nodes, `drawGraph` abandons PRD §5.5's force constants for
 * large-graph ones, stops labelling individual signals, and shortens the tick
 * budget. Named rather than inline because the module docstring cites it and a
 * literal in one branch of one function is not something a reader can find.
 *
 * 150 is where the PRD constants stop producing a readable picture, judged by
 * rendering: repulsion 8,000 against attraction 0.0008 spreads a dozen signals
 * into a constellation and a thousand into a grey wash.
 */
const LARGE_GRAPH_NODES = 150;

/**
 * Total SVG elements — nodes *plus* edges — past which a draw is worth noting
 * in the console as a likely slow one.
 *
 * **Elements, not nodes.** This replaced a `nodes.length > 500` warning that
 * quoted PRD §7.4. Once the server began capping `mode=signals` at exactly 500
 * nodes that condition became unreachable there, leaving `mode=tags` — which
 * caps at 5,000 and is *designed* to exceed 500 — as the only case that could
 * still fire it. So the sole surviving trigger was the one where the warning was
 * wrong, telling an operator a working graph had breached a threshold built to
 * be breached. Edges are what the renderer actually pays for: one `<line>` each,
 * and they outnumber nodes by orders of magnitude in the projection.
 */
const SVG_ELEMENT_WARN = 20_000;

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 5;
const NODE_MIN = 8;
const NODE_MAX = 14;
const EDGE_W_MIN = 1;
const EDGE_W_MAX = 4;

const STEEPLES_DIMS = [
  "social", "technological", "economic", "environmental",
  "political", "legal", "ethical", "security",
] as const;

/**
 * Graph View opens on a rolling window rather than the whole corpus: in
 * `mode=signals` edge generation is O(n²) server-side, and the force layout is
 * computed up front on the main thread in both modes, so an unbounded default
 * gets slower with every collection run.
 *
 * This is a readability default, not a safety mechanism — the server's caps are
 * that, and **All time** deliberately clears the window. A capped view says what
 * it left out rather than pretending it drew everything.
 */
const DEFAULT_RANGE_MONTHS = 1;

/** localStorage key for the one-time "why is the graph date-limited" notice. */
const RANGE_HINT_KEY = "kassandra-graph-range-hint";

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

// ── Module state ─────────────────────────────────────────────────────────────

let _svg: d3.Selection<SVGSVGElement, unknown, null, undefined> | null = null;
let _zoom: d3.ZoomBehavior<SVGSVGElement, unknown> | null = null;
let _g: d3.Selection<SVGGElement, unknown, null, undefined> | null = null;
let _tooltip: HTMLElement | null = null;
let _filterState: FilterState = {
  mode: "signals",
  from: "", to: "", wikilinks: [], workstreams: [], steeples_dim: "", steeples_min: 0,
};

// True once the default window has been seeded for this SPA session. Navigating
// away and back keeps the user's last-applied range; only a full page reload
// resets this and re-seeds the default.
let _filtersSeeded = false;

// ── Entry point ──────────────────────────────────────────────────────────────

export function renderGraphPage(root: HTMLElement): void {
  // The default window ends on *today* in the app zone. Warmed rather than
  // awaited: this function is synchronous, and the seeding below runs after the
  // mobile early-return, by which point a cached zone is usually already here.
  void ensureAppTimezone();
  root.appendChild(renderHeader());

  /* Mobile: graph not supported.
   *
   * `matchMedia`, not `window.innerWidth < 768`, and the difference is not
   * stylistic. The stylesheet switches this app to its mobile layout *at* 768
   * — `max-width: 768px` includes 768 — while `< 768` excludes it, so an iPad
   * Mini in portrait got the desktop graph inside a mobile shell, with 7 of 11
   * nodes off the canvas. One query, one boundary, matching the CSS.
   *
   * It also listened only once, at render, so rotating a tablet or dragging a
   * window across the boundary left whichever version had been chosen first:
   * a desktop graph and its 218px control panel in a 700px viewport. The
   * listener re-renders the route when the boundary is actually crossed, which
   * is what `navigate` to the current path does. */
  const mobile = window.matchMedia("(max-width: 768px)");
  const onBoundaryChange = () => navigate(location.pathname);
  mobile.addEventListener("change", onBoundaryChange);
  registerPageCleanup(() => mobile.removeEventListener("change", onBoundaryChange));

  if (mobile.matches) {
    const notice = document.createElement("div");
    notice.className = "gv-mobile-notice";
    notice.innerHTML = `
      <div class="gv-mobile-inner">
        <p class="gv-mobile-title">Graph View is optimised for desktop.</p>
        <p>Please open Kassandra on a larger screen for the best experience.</p>
      </div>`;
    root.appendChild(notice);
    return;
  }

  /* Re-fit on resize. The simulation is pre-ticked and then stopped, so node
   * positions are baked and a resize cannot move them — but the *view* onto
   * them is a zoom transform computed against the old size, which is why the
   * graph drifted off-centre when a window changed. Re-fitting recomputes that
   * transform from the bounding box and costs no simulation work.
   *
   * Debounced because a drag-resize fires this continuously, and registered
   * after the early-return so a mobile visit never installs it. */
  let resizeTimer: number | null = null;
  const onResize = () => {
    if (resizeTimer !== null) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => fitView(), 150);
  };
  window.addEventListener("resize", onResize);
  registerPageCleanup(() => {
    window.removeEventListener("resize", onResize);
    if (resizeTimer !== null) window.clearTimeout(resizeTimer);
  });

  // Seeded after the mobile early-return, so a mobile visit never consumes the
  // first-visit state and a later desktop render still gets the default window.
  if (!_filtersSeeded) {
    const { from, to } = defaultRange();
    _filterState = {
      mode: "signals",
      from, to, wikilinks: [], workstreams: [], steeples_dim: "", steeples_min: 0,
    };
    _filtersSeeded = true;
  }

  const shell = document.createElement("div");
  shell.className = "gv-shell";
  shell.innerHTML = `
    <div class="gv-canvas-wrap" id="gv-canvas-wrap">
      <div class="gv-loading-state">
        <div class="db-spinner"></div>
        <span>Loading graph…</span>
      </div>
    </div>
    <div class="gv-controls" id="gv-controls"></div>
    <div class="gv-tooltip" id="gv-tooltip"></div>
  `;
  root.appendChild(shell);

  _tooltip = shell.querySelector<HTMLElement>("#gv-tooltip");
  _svg = null;
  _g = null;
  _zoom = null;

  buildControls(shell.querySelector<HTMLElement>("#gv-controls")!);
  // The checkbox lists only exist once loadFilterOptions resolves, so the
  // second sync is what restores checked wikilinks/workstreams.
  loadFilterOptions().then(() => {
    syncControlsFromState();
    return loadAndDraw();
  });
}

// ── Controls panel ───────────────────────────────────────────────────────────

function buildControls(container: HTMLElement): void {
  container.innerHTML = `
    <div class="gv-ctrl-btns">
      <button class="btn btn-sm" id="gv-zoom-in"     title="Zoom In">+</button>
      <button class="btn btn-sm" id="gv-zoom-out"    title="Zoom Out">−</button>
      <button class="btn btn-sm" id="gv-fit"         title="Fit to Screen">Fit</button>
      <button class="btn btn-sm" id="gv-reset-layout" title="Reset Layout">Reset</button>
    </div>
    ${hintDismissed() ? "" : `
    <div class="gv-range-hint" id="gv-range-hint">
      <p class="gv-range-hint-text">
        Showing the last month of signals so the layout stays readable.
        Change the window under Filters.
      </p>
      <div class="gv-range-hint-actions">
        <button class="gv-range-hint-btn" id="gv-hint-all">Show all time</button>
        <button class="gv-range-hint-btn" id="gv-hint-ok">Got it</button>
      </div>
    </div>`}
    <details class="gv-filter-panel" id="gv-filter-panel">
      <summary class="gv-filter-summary">
        <span class="gv-filter-summary-text">Filters</span>
        <span class="gv-range-badge" id="gv-range-badge"></span>
      </summary>
      <div class="gv-filter-body">
        <div class="gv-filter-group">
          <label class="gv-filter-label">View</label>
          <div class="gv-mode-row" id="gv-mode-row">
            <button class="gv-mode-btn" data-mode="signals"
              title="Signals joined to each other where they share a tag. The richer picture, but it grows with the square of the window.">Signals</button>
            <button class="gv-mode-btn" data-mode="tags"
              title="Signals and tags both as nodes, joined by which signal carries which tag. Grows with the number of signals, so it holds a far larger window.">Signals &amp; tags</button>
          </div>
        </div>
        <div class="gv-filter-group">
          <div class="gv-filter-label-row">
            <label class="gv-filter-label">Date range</label>
            <button class="gv-link-btn" id="gv-all-time">All time</button>
          </div>
          <div class="gv-date-row">
            <input type="date" id="gv-from" class="gv-date-input">
            <input type="date" id="gv-to"   class="gv-date-input">
          </div>
          <p class="gv-date-hint">Defaults to the last month.</p>
        </div>
        <div class="gv-filter-group">
          <label class="gv-filter-label">Wikilinks</label>
          <div class="gv-checklist-scroll" id="gv-wl-list">
            <span class="gv-loading-label">Loading…</span>
          </div>
        </div>
        <div class="gv-filter-group">
          <label class="gv-filter-label">STEEPLES</label>
          <div class="gv-steeples-row">
            <select id="gv-steeples-dim" class="gv-select">
              <option value="">Any dimension</option>
              ${STEEPLES_DIMS.map(d => `<option value="${d}">${cap(d)}</option>`).join("")}
            </select>
            <input type="number" id="gv-steeples-min" class="gv-steeples-min"
              min="0" max="10" value="0" placeholder="Min">
          </div>
        </div>
        <div class="gv-filter-group">
          <label class="gv-filter-label">Workstreams</label>
          <div class="gv-checklist-scroll" id="gv-ws-list">
            <span class="gv-loading-label">Loading…</span>
          </div>
        </div>
        <div class="gv-filter-actions">
          <button class="btn btn-primary btn-sm" id="gv-apply">Apply</button>
          <button class="btn btn-sm"             id="gv-clear"
            title="Reset filters to the default one-month window">Clear</button>
        </div>
      </div>
    </details>`;

  container.querySelector("#gv-zoom-in")!.addEventListener("click", () => zoomBy(1.5));
  container.querySelector("#gv-zoom-out")!.addEventListener("click", () => zoomBy(1 / 1.5));
  container.querySelector("#gv-fit")!.addEventListener("click", () => fitView());
  container.querySelector("#gv-reset-layout")!.addEventListener("click", () => loadAndDraw());
  container.querySelector("#gv-apply")!.addEventListener("click", () => applyFilters());
  container.querySelector("#gv-clear")!.addEventListener("click", () => clearFilters());
  container.querySelector("#gv-all-time")!.addEventListener("click", () => showAllTime());
  // Applies at once rather than waiting for "Apply": the other controls narrow
  // what is drawn, this one changes what the picture *is*, and someone reaching
  // for it is usually reaching for it because the current view will not draw.
  container.querySelectorAll<HTMLButtonElement>(".gv-mode-btn").forEach(btn => {
    btn.addEventListener("click", () => {
      const next = (btn.dataset.mode === "tags" ? "tags" : "signals") as GraphMode;
      if (next === _filterState.mode) return;
      _filterState = { ..._filterState, mode: next };
      syncControlsFromState();
      loadAndDraw();
    });
  });
  // Optional-chained: the hint is absent once dismissed, and a `!` here would
  // throw at runtime and abort the rest of this function on every later visit.
  container.querySelector("#gv-hint-all")?.addEventListener("click", () => {
    dismissHint();
    showAllTime();
  });
  container.querySelector("#gv-hint-ok")?.addEventListener("click", () => dismissHint());

  syncControlsFromState();
}

/**
 * Push _filterState into the panel inputs so the panel and the drawn graph can
 * never disagree. Safe to call twice: loadFilterOptions() injects the checkbox
 * lists asynchronously, after buildControls has already returned.
 */
function syncControlsFromState(): void {
  const fromEl = document.getElementById("gv-from") as HTMLInputElement | null;
  const toEl   = document.getElementById("gv-to")   as HTMLInputElement | null;
  if (fromEl) fromEl.value = _filterState.from;
  if (toEl)   toEl.value   = _filterState.to;

  document.querySelectorAll<HTMLButtonElement>(".gv-mode-btn").forEach(b => {
    b.classList.toggle("gv-mode-btn-on", b.dataset.mode === _filterState.mode);
  });

  const dimEl = document.getElementById("gv-steeples-dim") as HTMLSelectElement | null;
  const minEl = document.getElementById("gv-steeples-min") as HTMLInputElement | null;
  if (dimEl) dimEl.value = _filterState.steeples_dim;
  if (minEl) minEl.value = String(_filterState.steeples_min);

  const wl = new Set(_filterState.wikilinks);
  document.querySelectorAll<HTMLInputElement>(".gv-wl-check")
    .forEach(el => { el.checked = wl.has(el.value); });
  const ws = new Set(_filterState.workstreams);
  document.querySelectorAll<HTMLInputElement>(".gv-ws-check")
    .forEach(el => { el.checked = ws.has(el.value); });

  updateRangeBadge();
}

/** Keep the collapsed panel honest about which window is actually drawn. */
function updateRangeBadge(): void {
  const badge = document.getElementById("gv-range-badge");
  if (!badge) return;
  const label = rangeLabel(_filterState.from, _filterState.to);
  badge.textContent = label;
  badge.title = `Active date range: ${label}`;
  badge.classList.toggle("gv-range-badge-all", !_filterState.from && !_filterState.to);
}

// ── Filter options ────────────────────────────────────────────────────────────

async function loadFilterOptions(): Promise<void> {
  try {
    const [wikilinks, workstreams] = await Promise.all([
      api<WikilinkItem[]>("/wikilinks"),
      api<WorkstreamItem[]>("/workstreams"),
    ]);

    const wlList = document.getElementById("gv-wl-list");
    if (wlList) {
      wlList.innerHTML = wikilinks.length === 0
        ? `<span class="gv-loading-label">No wikilinks.</span>`
        : wikilinks.map(w => `
          <label class="gv-check-item">
            <input type="checkbox" value="${esc(w.name)}" class="gv-wl-check">
            <span class="gv-check-label">${esc(w.name)}</span>
            <span class="gv-check-count">${w.signalCount}</span>
          </label>`).join("");
    }

    const wsList = document.getElementById("gv-ws-list");
    if (wsList) {
      wsList.innerHTML = workstreams.length === 0
        ? `<span class="gv-loading-label">No workstreams.</span>`
        : workstreams.map(ws => `
          <label class="gv-check-item">
            <input type="checkbox" value="${esc(ws.id)}" class="gv-ws-check">
            <span class="gv-check-label">${esc(ws.name)}</span>
          </label>`).join("");
    }
  } catch {
    // Non-fatal — graph draws without filter options populated
  }
}

// ── Filter actions ────────────────────────────────────────────────────────────

function applyFilters(): void {
  _filterState.from = (document.getElementById("gv-from") as HTMLInputElement).value;
  _filterState.to   = (document.getElementById("gv-to")   as HTMLInputElement).value;
  _filterState.wikilinks = Array.from(
    document.querySelectorAll<HTMLInputElement>(".gv-wl-check:checked"),
  ).map(el => el.value);
  _filterState.workstreams = Array.from(
    document.querySelectorAll<HTMLInputElement>(".gv-ws-check:checked"),
  ).map(el => el.value);
  _filterState.steeples_dim = (document.getElementById("gv-steeples-dim") as HTMLSelectElement).value;
  _filterState.steeples_min = parseInt(
    (document.getElementById("gv-steeples-min") as HTMLInputElement).value || "0", 10,
  );

  const panel = document.getElementById("gv-filter-panel") as HTMLDetailsElement | null;
  if (panel) panel.open = false;

  loadAndDraw();
}

/** Reset to the default state — the one-month window, nothing else applied. */
function clearFilters(): void {
  const { from, to } = defaultRange();
  _filterState = {
    // Mode survives a clear. It is which graph you are looking at, not a filter
    // applied to one, and being thrown back to the signal view — the view that
    // cannot draw a large window — is the opposite of what "clear" should do
    // for someone who switched to tags precisely because their window is big.
    mode: _filterState.mode,
    from, to, wikilinks: [], workstreams: [], steeples_dim: "", steeples_min: 0,
  };
  syncControlsFromState();
  loadAndDraw();
}

/** Blank the dates only; wikilink/workstream/STEEPLES filters are left alone. */
function showAllTime(): void {
  _filterState.from = "";
  _filterState.to   = "";
  const fromEl = document.getElementById("gv-from") as HTMLInputElement | null;
  const toEl   = document.getElementById("gv-to")   as HTMLInputElement | null;
  if (fromEl) fromEl.value = "";
  if (toEl)   toEl.value   = "";
  loadAndDraw();
}

// ── Zoom helpers ──────────────────────────────────────────────────────────────

function zoomBy(factor: number): void {
  if (!_svg || !_zoom) return;
  _svg.transition().duration(250).call(_zoom.scaleBy, factor);
}

function fitView(): void {
  if (!_svg || !_zoom || !_g) return;
  const gNode = _g.node();
  if (!gNode) return;
  /* `getBBox()` on a detached SVG throws `NotSupportedError: Could not resolve
   * relative length` in Chromium, because this SVG is sized `width="100%"` and
   * a percentage has nothing to resolve against outside the document. That is
   * reachable from the `requestAnimationFrame` at the end of `drawGraph`: a
   * filter change between the frame being queued and it running replaces the
   * canvas, leaving this measuring an orphan. Nothing to recover — there is no
   * view to fit — so check, and keep the throw from escaping if it races. */
  if (!gNode.isConnected) return;
  let bbox: DOMRect;
  try {
    bbox = gNode.getBBox();
  } catch {
    return;
  }
  if (bbox.width === 0 || bbox.height === 0) return;
  const svgEl = _svg.node();
  if (!svgEl) return;
  const w = svgEl.clientWidth;
  const h = svgEl.clientHeight;
  const scale = Math.min(0.9 / (bbox.width / w), 0.9 / (bbox.height / h), ZOOM_MAX);
  const tx = w / 2 - scale * (bbox.x + bbox.width  / 2);
  const ty = h / 2 - scale * (bbox.y + bbox.height / 2);
  _svg.transition().duration(500).call(
    _zoom.transform,
    d3.zoomIdentity.translate(tx, ty).scale(scale),
  );
}

function resetView(): void {
  if (!_svg || !_zoom) return;
  _svg.transition().duration(500).call(_zoom.transform, d3.zoomIdentity);
}

// ── Data fetch & draw ─────────────────────────────────────────────────────────

async function loadAndDraw(): Promise<void> {
  const canvasWrap = document.getElementById("gv-canvas-wrap");
  if (!canvasWrap) return;

  // Every filter change funnels through here, so the badge cannot go stale.
  updateRangeBadge();

  _svg  = null;
  _g    = null;
  _zoom = null;

  canvasWrap.innerHTML = `
    <div class="gv-loading-state">
      <div class="db-spinner"></div>
      <span>Loading graph…</span>
    </div>`;

  const params = new URLSearchParams();
  if (_filterState.mode === "tags") params.set("mode", "tags");
  if (_filterState.from) params.set("from", _filterState.from);
  if (_filterState.to)   params.set("to",   _filterState.to);
  if (_filterState.wikilinks.length > 0)
    params.set("wikilinks", _filterState.wikilinks.join(","));
  if (_filterState.workstreams.length > 0)
    params.set("workstreams", _filterState.workstreams.join(","));
  if (_filterState.steeples_dim && _filterState.steeples_min > 0) {
    params.set("steeples_dim", _filterState.steeples_dim);
    params.set("steeples_min", String(_filterState.steeples_min));
  }

  /* Two `try`s, not one, and the reason is worth keeping.
   *
   * These used to be a single block with a bare `catch` that rewrote every
   * exception as "Failed to load graph data." On 2026-09-10 the graph had been
   * failing on a `RangeError` thrown *after* the data arrived intact, and the
   * message sent three separate investigations at the server — which was
   * returning 200 every time. The error was discarded, so there was nothing to
   * read. Log what was caught, and say which half failed. */
  const qs = params.toString();
  let data: GraphData;
  try {
    data = await api<GraphData>(`/graph${qs ? `?${qs}` : ""}`);
  } catch (err) {
    console.error("[Graph] request failed", err);
    canvasWrap.innerHTML = `
      <div class="gv-error"><p>Could not reach the server for graph data.</p></div>`;
    return;
  }

  try {
    drawGraph(data, canvasWrap);
  } catch (err) {
    console.error(
      `[Graph] render failed with ${data.nodes.length} node(s) and ` +
      `${data.edges.length} edge(s)`, err,
    );
    canvasWrap.innerHTML = `
      <div class="gv-error">
        <p>The graph data loaded, but could not be drawn.</p>
        <p class="gv-error-detail">${data.nodes.length.toLocaleString()} signals and
        ${data.edges.length.toLocaleString()} connections. Try a shorter date range,
        or switch to the tag view.</p>
      </div>`;
  }
}

/**
 * Say what was left out, when anything was.
 *
 * The alternative is drawing the most recent 500 of 3,000 signals and letting
 * the picture imply it covered the window — which is worse than an error,
 * because it looks like an answer. Same posture as the daily brief's
 * dropped-signal note: naming the gap beats silently covering a fraction.
 *
 * In `signals` mode it also points at the tag view, because that is the honest
 * next move: the cap is a property of the projection, not of the corpus, and
 * the other view can hold the whole window.
 */
function renderCapNotice(canvasWrap: HTMLElement, data: GraphData): void {
  if (!data.capped && !data.edgesCapped) return;

  const parts: string[] = [];
  if (data.capped) {
    parts.push(`the ${data.nodes.filter(n => n.kind === "signal").length.toLocaleString()}
      most recent of ${data.total.toLocaleString()} signals`);
  }
  if (data.edgesCapped) {
    parts.push(`the ${data.edges.length.toLocaleString()} strongest of
      ${data.edgeTotal.toLocaleString()} connections`);
  }

  /* The call to action is tied to `capped`, not to `edgesCapped`, because only
   * one of them means the picture is incomplete. Dropping the weakest edges
   * keeps every signal — the graph still answers "what is here" — whereas
   * dropping signals does not. Telling someone who has already narrowed to
   * three days to narrow further, because 7,215 near-identical single-tag
   * links were trimmed to the strongest 4,000, is advice that fixes nothing. */
  const el = document.createElement("div");
  el.className = "gv-cap-notice";
  el.innerHTML = `<span>Showing ${parts.join(" and ")}.</span>` + (
    !data.capped ? ""
      : data.mode === "signals"
        ? ` <button class="gv-link-btn" id="gv-cap-tags">Switch to the tag view</button>
            <span class="gv-cap-muted">to see the whole window, or narrow the date range.</span>`
        : ` <span class="gv-cap-muted">Narrow the date range to see more.</span>`
  );
  canvasWrap.appendChild(el);

  el.querySelector("#gv-cap-tags")?.addEventListener("click", () => {
    _filterState = { ..._filterState, mode: "tags" };
    syncControlsFromState();
    loadAndDraw();
  });
}

function drawGraph(data: GraphData, canvasWrap: HTMLElement): void {
  const width  = canvasWrap.clientWidth  || window.innerWidth;
  const height = canvasWrap.clientHeight || window.innerHeight - 56;

  if (data.nodes.length === 0) {
    renderEmptyState(canvasWrap);
    return;
  }

  const elements = data.nodes.length + data.edges.length;
  if (elements > SVG_ELEMENT_WARN) {
    // The remedy depends on where you already are. Telling someone in the tag
    // view to "use the tag view" is the same class of mistake as the 500-node
    // warning this replaced: advice that fires precisely where it is wrong.
    console.warn(
      `[Graph] ${elements.toLocaleString()} SVG elements ` +
      `(${data.nodes.length.toLocaleString()} nodes, ` +
      `${data.edges.length.toLocaleString()} edges) — expect a slow draw. ` +
      "Canvas rendering is not implemented; " +
      (data.mode === "tags"
        ? "narrow the date range."
        : "narrow the date range or switch to the tag view."),
    );
  }

  canvasWrap.innerHTML = "";
  renderCapNotice(canvasWrap, data);

  // ── Scales ────────────────────────────────────────────────────────────────

  const accentColor = getCSSVar("--accent-primary");
  const mutedColor  = getCSSVar("--text-muted");
  const bgColor     = getCSSVar("--bg-primary");

  /* `d3.min`/`d3.max` rather than `Math.min(...array)` throughout this function,
   * and it is a correctness fix rather than a style one. Spreading passes one
   * *argument* per element, and V8's argument limit is between 100k and 200k —
   * measured, not assumed. Node count never came close, but the edge array did:
   * a 14-day window on a corpus of this density produced 171,767 edges, and
   * `Math.max(...edges.map(e => e.weight), 1)` threw
   * `RangeError: Maximum call stack size exceeded` in the browser. The server
   * caps edges now, so this is belt and braces — but the cap is a constant
   * someone will raise, and this is the line that would have failed silently
   * again when they did. */
  const dates = data.nodes.map(n => new Date(n.datePublished).getTime());
  const minDate = d3.min(dates) ?? 0;
  const maxDate = d3.max(dates) ?? 0;

  const colorScale = d3
    .scaleSequential(d3.interpolateRgb(mutedColor, accentColor))
    .domain([minDate, maxDate > minDate ? maxDate : minDate + 1]);

  const maxNodeSize = Math.max(d3.max(data.nodes, n => n.size) ?? 1, 1);
  const sizeScale   = d3.scaleSqrt().domain([0, maxNodeSize]).range([NODE_MIN, NODE_MAX]);

  /* Tag nodes get their own scale, and it is not the signal scale with a bigger
   * range. A signal's `size` is its tag count (3–8, per analysis.md); a tag's is
   * how many of these signals carry it, which runs into the hundreds. Sharing
   * one domain would flatten every signal to the minimum radius. Declared here,
   * beside the other scales, because the collision force below needs it. */
  const tagSizeScale = d3.scaleSqrt()
    .domain([0, Math.max(d3.max(data.nodes.filter(n => n.kind === "wikilink"), n => n.size) ?? 1, 1)])
    .range([NODE_MIN + 4, NODE_MAX * 2.2]);

  const maxEdgeWeight = Math.max(d3.max(data.edges, e => e.weight) ?? 1, 1);
  const edgeScale     = d3.scaleLinear().domain([1, maxEdgeWeight]).range([EDGE_W_MIN, EDGE_W_MAX]);

  // ── D3 node / edge data ───────────────────────────────────────────────────

  const nodes: NodeDatum[] = data.nodes.map(n => ({
    ...n,
    x: width  / 2 + (Math.random() - 0.5) * 300,
    y: height / 2 + (Math.random() - 0.5) * 300,
  }));

  const nodeById = new Map<string, NodeDatum>(nodes.map(n => [n.id, n]));

  // Pre-resolve source/target to NodeDatum objects so D3 skips string lookup
  const edges: EdgeDatum[] = data.edges.flatMap(e => {
    const src = nodeById.get(e.source);
    const tgt = nodeById.get(e.target);
    if (!src || !tgt) return [];
    return [{
      source: src,
      target: tgt,
      weight: e.weight,
      sharedWikilinks: e.sharedWikilinks,
    }];
  });

  // ── Force simulation ──────────────────────────────────────────────────────

  /* Two force configurations, chosen by size, because one set of constants
   * cannot serve both.
   *
   * FORCE_CONFIG (PRD §5.5) was tuned against a corpus of about a dozen
   * signals: repulsion 8,000 with attraction 0.0008 spreads that handful into a
   * readable constellation. Applied to the 1,234 nodes this view can now hold,
   * the same numbers blow the layout apart — every node shoving every other
   * with no link strong enough to pull it back — and `fitView` then zooms out
   * until the whole thing is a grey wash of crossing lines. Verified by
   * screenshot before this branch existed: the tag view was unreadable.
   *
   * Above the threshold the layout switches to conventional large-graph
   * settings: repulsion two orders of magnitude weaker, links that actually
   * pull, positioning forces toward the centre instead of one weak centring
   * force, and collision so nodes do not stack. `distanceMax` matters as much
   * as strength — without it every node repels every other at any distance,
   * which is both the sprawl and most of the per-tick cost.
   *
   * Small graphs keep the old constants exactly, so the view an analyst is used
   * to at a few days' range is unchanged. */
  const big = nodes.length > LARGE_GRAPH_NODES;

  const sim = d3
    .forceSimulation<NodeDatum>(nodes)
    .force(
      "link",
      big
        ? d3.forceLink<NodeDatum, EdgeDatum>(edges).id(d => d.id).distance(45).strength(0.35)
        : d3.forceLink<NodeDatum, EdgeDatum>(edges).id(d => d.id)
            .strength(FORCE_CONFIG.attractionStrength),
    )
    .force(
      "charge",
      big
        ? d3.forceManyBody<NodeDatum>().strength(-90).distanceMax(400)
        : d3.forceManyBody<NodeDatum>()
            .strength(-FORCE_CONFIG.repulsionStrength)
            .distanceMin(FORCE_CONFIG.minDistance),
    )
    .velocityDecay(FORCE_CONFIG.damping)
    .stop();

  if (big) {
    // forceX/forceY rather than forceCenter: centring only translates the whole
    // layout, which does nothing about a cloud that is simply too large.
    sim.force("x", d3.forceX<NodeDatum>(width / 2).strength(0.06))
       .force("y", d3.forceY<NodeDatum>(height / 2).strength(0.06))
       .force("collide", d3.forceCollide<NodeDatum>()
         .radius(d => (d.kind === "wikilink" ? tagSizeScale(d.size) : sizeScale(d.size)) + 2));
  } else {
    sim.force("center", d3.forceCenter<NodeDatum>(width / 2, height / 2)
      .strength(FORCE_CONFIG.centerForce));
  }

  /* Pre-compute layout synchronously (tick handler not yet registered).
   *
   * The tick count is scaled rather than fixed, because this loop blocks the
   * main thread and each tick costs roughly O(nodes log nodes + edges). 500
   * ticks over a few dozen nodes is imperceptible; 500 over the tens of
   * thousands this view can now legitimately hold is a frozen tab, which reads
   * to the user as the same failure the error box used to report. A layout that
   * is nearly settled and appears beats a perfect one that never does. */
  const work = nodes.length + edges.length;
  const iterations = work <= 2_000 ? FORCE_CONFIG.iterations
                   : work <= 10_000 ? 200
                   : work <= 40_000 ? 90
                   : 45;
  for (let i = 0; i < iterations; i++) sim.tick();

  // ── SVG canvas ────────────────────────────────────────────────────────────

  const svgNode = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svgNode.setAttribute("width",  "100%");
  svgNode.setAttribute("height", "100%");
  svgNode.style.background = bgColor || "var(--bg-primary)";

  _svg = d3.select<SVGSVGElement, unknown>(svgNode);
  _g   = _svg.append("g");

  // ── Zoom / pan ────────────────────────────────────────────────────────────

  /* The handler closes over *this* draw's `g`, rather than reading the module's
   * `_g!`. `fitView` runs a 500ms zoom transition; if a filter changes while it
   * is still running, `loadAndDraw` sets `_g = null` and the in-flight
   * transition then ticks into `_g!.attr(...)` and throws
   * `TypeError: Cannot read properties of null`. The non-null assertion was
   * simply wrong — the value genuinely is null at that moment — and the throw
   * landed outside every try, so it surfaced only in the console. A closure
   * cannot go null: the orphaned transition harmlessly transforms the detached
   * group it was started on. */
  const gForZoom = _g;
  _zoom = d3
    .zoom<SVGSVGElement, unknown>()
    /* An explicit extent, because d3-zoom's default one reads
     * `svg.width.baseVal.value` — and this SVG is sized `width="100%"`, so that
     * read throws `NotSupportedError: Could not resolve relative length` the
     * moment the element is detached. A zoom transition outliving its canvas is
     * routine here (filter changes cancel `fitView` mid-flight), so the default
     * was a latent throw on an ordinary interaction. `width`/`height` are
     * already measured above; handing them over sidesteps the read. */
    .extent([[0, 0], [width, height]])
    .scaleExtent([ZOOM_MIN, ZOOM_MAX])
    .on("zoom", event => {
      gForZoom.attr("transform", (event as d3.D3ZoomEvent<SVGSVGElement, unknown>).transform.toString());
    });

  _svg.call(_zoom);
  // Override built-in dblclick-zoom with view-reset
  _svg.on("dblclick.zoom", null).on("dblclick", () => resetView());

  // ── Drag behaviour ────────────────────────────────────────────────────────

  const drag = d3
    .drag<SVGGElement, NodeDatum>()
    .on("start", (event, d) => {
      if (!event.active) sim.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    })
    .on("drag", (event, d) => {
      d.fx = event.x;
      d.fy = event.y;
    })
    .on("end", (event, d) => {
      if (!event.active) sim.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    });

  // ── Draw edges ────────────────────────────────────────────────────────────

  const linkSel = _g
    .append("g")
    .attr("class", "gv-links")
    .selectAll<SVGLineElement, EdgeDatum>("line")
    .data(edges)
    .join("line")
    .attr("class", "gv-link")
    .attr("stroke", mutedColor)
    .attr("stroke-opacity", 0.4)
    .attr("stroke-width", d => edgeScale(d.weight))
    .attr("x1", d => asNode(d.source).x ?? 0)
    .attr("y1", d => asNode(d.source).y ?? 0)
    .attr("x2", d => asNode(d.target).x ?? 0)
    .attr("y2", d => asNode(d.target).y ?? 0);

  // Edge hover: tooltip showing shared wikilinks
  linkSel
    .on("mouseenter", (event: MouseEvent, d) => {
      if (!_tooltip) return;
      _tooltip.innerHTML = d.sharedWikilinks
        .map(w => `<span class="gv-wl-chip">${esc(w)}</span>`)
        .join(" ");
      _tooltip.style.display = "flex";
      positionTooltip(event);
    })
    .on("mousemove", (event: MouseEvent) => positionTooltip(event))
    .on("mouseleave", () => { if (_tooltip) _tooltip.style.display = "none"; });

  // ── Draw nodes ────────────────────────────────────────────────────────────

  // Build adjacency set for hover highlight
  const adjacentIds = new Map<string, Set<string>>(
    nodes.map(n => [n.id, new Set<string>()]),
  );
  edges.forEach(e => {
    const srcId = asNode(e.source).id;
    const tgtId = asNode(e.target).id;
    adjacentIds.get(srcId)?.add(tgtId);
    adjacentIds.get(tgtId)?.add(srcId);
  });

  const nodeSel = _g
    .append("g")
    .attr("class", "gv-nodes")
    .selectAll<SVGGElement, NodeDatum>("g")
    .data(nodes)
    .join("g")
    .attr("class", "gv-node")
    .attr("transform", d => `translate(${d.x ?? 0},${d.y ?? 0})`)
    .call(drag);

  /* Tag nodes read differently from signal nodes on purpose. In `tags` mode the
   * two kinds are not peers: a handful of tags carry hundreds of signals each,
   * and the shape worth seeing is which tags are hubs and which signals bridge
   * them. Sizing tags by how many of *these* signals carry them, always
   * labelling them, and colouring them by accent rather than by date does that.
   * Date colour would be meaningless on a tag anyway — it has no date. */
  nodeSel
    .append("circle")
    .attr("r", d => d.kind === "wikilink" ? tagSizeScale(d.size) : sizeScale(d.size))
    .attr("fill", d => d.kind === "wikilink"
      ? (accentColor || "var(--accent-primary)")
      : colorScale(new Date(d.datePublished).getTime()))
    .attr("fill-opacity",  d => d.kind === "wikilink" ? 0.55 : 1)
    .attr("stroke",       bgColor || "var(--bg-primary)")
    .attr("stroke-width", 1.5);

  /* Above the threshold, only tags are labelled.
   *
   * A label per node is right for a dozen nodes and actively harmful for a
   * thousand: verified by screenshot, 1,200 overlapping titles rendered as a
   * band of grey noise that hid the very structure the view exists to show.
   * Tag labels stay because they are the landmarks — an unlabelled hub is just
   * a big circle — and a signal's title is a hover away, which is how you read
   * an individual node at this scale anyway. */
  nodeSel
    .append("text")
    .attr("class", d => d.kind === "wikilink" ? "gv-node-label gv-tag-label" : "gv-node-label")
    .attr("dy", d => (d.kind === "wikilink" ? tagSizeScale(d.size) : sizeScale(d.size)) + 12)
    .attr("text-anchor", "middle")
    .text(d => d.kind === "wikilink" ? d.title
             : big ? ""
             : truncate(d.title, 24));

  // Node hover: tooltip + dim unconnected nodes/edges
  nodeSel
    .on("mouseenter", (event: MouseEvent, d) => {
      const neighbours = adjacentIds.get(d.id) ?? new Set<string>();
      nodeSel.attr("opacity", n =>
        n.id === d.id || neighbours.has(n.id) ? 1 : 0.15,
      );
      linkSel.attr("stroke-opacity", e =>
        asNode(e.source).id === d.id || asNode(e.target).id === d.id ? 0.85 : 0.05,
      );
      if (_tooltip) {
        _tooltip.innerHTML = `<span class="gv-node-tip-title">${esc(d.title)}</span>`;
        _tooltip.style.display = "flex";
        positionTooltip(event);
      }
    })
    .on("mousemove", (event: MouseEvent) => positionTooltip(event))
    .on("mouseleave", () => {
      nodeSel.attr("opacity", 1);
      linkSel.attr("stroke-opacity", 0.4);
      if (_tooltip) _tooltip.style.display = "none";
    })
    .on("click", (_: MouseEvent, d) => {
      // Tag nodes have no signal to open — their id is a wikilink id, and
      // handing it to Folder View as `?signal=` would ask for a signal that
      // does not exist. Filtering the graph to that tag is the useful action.
      if (d.kind === "wikilink") {
        _filterState = { ..._filterState, wikilinks: [d.title] };
        syncControlsFromState();
        loadAndDraw();
        return;
      }
      // Navigate to Folder View with this signal pre-selected
      navigate(`/folders?signal=${d.id}`);
    });

  // ── Tick handler (fires during drag re-settling) ──────────────────────────

  sim.on("tick", () => {
    linkSel
      .attr("x1", d => asNode(d.source).x ?? 0)
      .attr("y1", d => asNode(d.source).y ?? 0)
      .attr("x2", d => asNode(d.target).x ?? 0)
      .attr("y2", d => asNode(d.target).y ?? 0);
    nodeSel.attr("transform", n => `translate(${n.x ?? 0},${n.y ?? 0})`);
  });

  canvasWrap.appendChild(svgNode);

  /* Fit once the SVG is in the document. The layout is scattered around
   * `width/2, height/2` and pre-ticked, and nothing ever framed it — so the
   * graph opened with nodes off the canvas at every width, 5 of 11 even at
   * 1280px, reachable only by panning or by pressing Fit. The rAF is needed
   * because `fitView` measures `clientWidth` and reads a bounding box, and
   * both are zero until after layout. */
  requestAnimationFrame(() => fitView());
}

/**
 * Nothing to draw. "No signals match the current filters" is the wrong story
 * when the only filter is the default date window and the corpus is simply
 * older than a month — that reads as a bug rather than an empty window. So the
 * copy and the offered actions branch on which kind of filter is actually on.
 */
function renderEmptyState(canvasWrap: HTMLElement): void {
  const ranged = Boolean(_filterState.from || _filterState.to);
  const filtered = _filterState.wikilinks.length > 0
    || _filterState.workstreams.length > 0
    || Boolean(_filterState.steeples_dim && _filterState.steeples_min > 0);

  const range = esc(rangeLabel(_filterState.from, _filterState.to));
  let title: string;
  let sub: string;

  if (ranged && !filtered) {
    title = "No signals published in this date range.";
    sub   = range;
  } else if (ranged) {
    title = "No signals match the current filters.";
    sub   = `Date range: ${range}`;
  } else if (filtered) {
    title = "No signals match the current filters.";
    sub   = "No date range applied.";
  } else {
    title = "No signals yet.";
    sub   = "Signals appear here once a collection run has stored some.";
  }

  canvasWrap.innerHTML = `
    <div class="gv-empty">
      <p>${title}</p>
      <p class="gv-empty-sub">${sub}</p>
      ${ranged || filtered ? `
      <div class="gv-empty-actions">
        ${ranged ? `<button id="gv-empty-all-time">Show all time</button>` : ""}
        ${filtered ? `<button id="gv-empty-clear">Reset filters</button>` : ""}
      </div>` : ""}
    </div>`;

  // Queried off the live element right after the assignment — these listeners
  // die with the nodes the next time loadAndDraw overwrites innerHTML.
  canvasWrap.querySelector("#gv-empty-all-time")
    ?.addEventListener("click", () => showAllTime());
  canvasWrap.querySelector("#gv-empty-clear")
    ?.addEventListener("click", () => clearFilters());
}

// ── Utilities ─────────────────────────────────────────────────────────────────

/** Cast D3 link source/target (resolved to NodeDatum by forceLink) */
function asNode(n: NodeDatum | string | number): NodeDatum {
  return n as NodeDatum;
}

function positionTooltip(event: MouseEvent): void {
  if (!_tooltip) return;
  const pad = 14;
  let x = event.clientX + pad;
  let y = event.clientY + pad;
  if (x + 240 > window.innerWidth)  x = event.clientX - 240 - pad;
  if (y + 80  > window.innerHeight) y = event.clientY - 80  - pad;
  _tooltip.style.left = `${x}px`;
  _tooltip.style.top  = `${y}px`;
}

function getCSSVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n)}…`;
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ── Date helpers ──────────────────────────────────────────────────────────────
//
// Calendar arithmetic on plain date parts, never on UTC instants: both
// `toISOString()` and `new Date("2026-08-14")` go through UTC, so either one
// shifts the calendar day for anyone off it.
//
// "Today" comes from the app time zone rather than the browser's, because that
// is the zone the server files signals under. A graph window ending on the
// browser's today would silently exclude, or invent, a day's worth of signals
// for anyone working outside the app zone. The parts arithmetic below is
// zone-independent once the starting date is right, so this is the one place
// the zone enters.

/** Local-time YYYY-MM-DD, the format the /graph from/to params expect. */
function toISODate(d: Date): string {
  const y   = d.getFullYear();
  const m   = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Today in the app zone, as a Date at local midnight for the helpers below. */
function todayInApp(): Date {
  return parseISODate(todayInAppTz()) ?? new Date();
}

/**
 * Same day-of-month `months` away, clamped to the target month's length.
 * A bare setMonth(m - 1) overflows — 31 Mar becomes 3 Mar — so the day is
 * capped at `new Date(y, m + 1, 0).getDate()`, i.e. day 0 of the following
 * month, which is the last day of the target one. Year-wrap and leap years
 * fall out of the Date constructor's own normalisation.
 */
function addMonths(d: Date, months: number): Date {
  const y = d.getFullYear();
  const m = d.getMonth() + months;
  const lastDayOfTarget = new Date(y, m + 1, 0).getDate();
  return new Date(y, m, Math.min(d.getDate(), lastDayOfTarget));
}

/** The rolling default window: one month back, to today, both inclusive. */
function defaultRange(): { from: string; to: string } {
  const today = todayInApp();
  return {
    from: toISODate(addMonths(today, -DEFAULT_RANGE_MONTHS)),
    to:   toISODate(today),
  };
}

/**
 * "2026-08-14" → Date at local midnight; null if not a plain ISO date.
 * slice() rather than regex capture groups: under noUncheckedIndexedAccess a
 * match group is `string | undefined` and would need guarding anyway.
 */
function parseISODate(s: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(
    Number(s.slice(0, 4)),
    Number(s.slice(5, 7)) - 1,
    Number(s.slice(8, 10)),
  );
  return isNaN(d.getTime()) ? null : d;
}

/** "14 Jul", or "14 Jul 2025" when the year is not `refYear`. */
function fmtRangeDate(iso: string, refYear: number): string {
  const d = parseISODate(iso);
  if (!d) return iso;
  const mon = MONTH_ABBR[d.getMonth()] ?? "";
  return d.getFullYear() === refYear
    ? `${d.getDate()} ${mon}`
    : `${d.getDate()} ${mon} ${d.getFullYear()}`;
}

/** Human label for the active window — drives the badge and the empty state. */
function rangeLabel(from: string, to: string): string {
  if (!from && !to) return "All time";
  const nowYear = todayInApp().getFullYear();
  const fy = parseISODate(from)?.getFullYear() ?? null;
  const ty = parseISODate(to)?.getFullYear() ?? null;
  // Suppress the year only when nothing straddles or precedes the current one.
  const ref = (fy === null || fy === nowYear) && (ty === null || ty === nowYear)
    ? nowYear
    : -1;
  if (from && to) return `${fmtRangeDate(from, ref)} – ${fmtRangeDate(to, ref)}`;
  if (from)       return `From ${fmtRangeDate(from, ref)}`;
  return `Until ${fmtRangeDate(to, ref)}`;
}

// ── First-visit notice ────────────────────────────────────────────────────────
//
// Same shape as lib/theme.ts, but guarded: Safari private mode throws on
// setItem, and an uncaught throw in the click handler would skip the remove()
// below and leave a notice that refuses to dismiss.

function hintDismissed(): boolean {
  try {
    return localStorage.getItem(RANGE_HINT_KEY) === "dismissed";
  } catch {
    return true; // Can't remember a dismissal, so never nag.
  }
}

function dismissHint(): void {
  try {
    localStorage.setItem(RANGE_HINT_KEY, "dismissed");
  } catch {
    // Non-fatal — the notice still goes away for this session.
  }
  document.getElementById("gv-range-hint")?.remove();
}
